import React, { useState, useEffect, useMemo } from 'react';
import { Type, Copy, Download, Shuffle, Eye, Settings, Zap, Code, List, ArrowUpDown } from 'lucide-react';

interface ConversionMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  example: string;
}

interface ConversionSettings {
  preserveNumbers: boolean;
  preserveAcronyms: boolean;
  handleSpecialChars: boolean;
  trimWhitespace: boolean;
  removeEmpty: boolean;
  removeDuplicates: boolean;
  ignoreCase: boolean;
  sortItems: boolean;
  reverseSort: boolean;
  addPrefix: string;
  addSuffix: string;
  minLength: number;
  maxLength: number;
  limitCount: boolean;
  maxCount: number;
  customDelimiters: string;
  preserveLeadingUnderscore: boolean;
  preserveTrailingUnderscore: boolean;
  forceFirstLowercase: boolean;
  detectAcronyms: boolean;
}

interface ConversionResult {
  inputItems: string[];
  finalItems: string[];
  method: string;
  output: string;
  stats: ConversionStatistics;
}

interface ConversionStatistics {
  originalCount: number;
  finalCount: number;
  emptyRemoved: number;
  duplicatesRemoved: number;
  totalInputLength: number;
  totalOutputLength: number;
  avgInputLength: number;
  avgOutputLength: number;
  longestInput: string;
  shortestInput: string;
  longestOutput: string;
  shortestOutput: string;
  conversionRatio: number;
}

const CamelCaseConverter: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('camelCase');
  const [outputText, setOutputText] = useState('');
  const [copyMessage, setCopyMessage] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);

  const [settings, setSettings] = useState<ConversionSettings>({
    preserveNumbers: true,
    preserveAcronyms: false,
    handleSpecialChars: true,
    trimWhitespace: true,
    removeEmpty: true,
    removeDuplicates: false,
    ignoreCase: false,
    sortItems: false,
    reverseSort: false,
    addPrefix: '',
    addSuffix: '',
    minLength: 0,
    maxLength: 200,
    limitCount: false,
    maxCount: 100,
    customDelimiters: '',
    preserveLeadingUnderscore: false,
    preserveTrailingUnderscore: false,
    forceFirstLowercase: true,
    detectAcronyms: true,
  });

  const conversionMethods: ConversionMethod[] = [
    {
      id: 'camelCase',
      name: 'camelCase',
      description: 'First word lowercase, capitalize subsequent words',
      icon: <Type className="w-4 h-4" />,
      color: 'blue',
      example: 'myVariableName'
    },
    {
      id: 'PascalCase',
      name: 'PascalCase',
      description: 'Capitalize first letter of each word',
      icon: <ArrowUpDown className="w-4 h-4" />,
      color: 'green',
      example: 'MyClassName'
    },
    {
      id: 'snake_case',
      name: 'snake_case',
      description: 'Lowercase with underscores',
      icon: <Code className="w-4 h-4" />,
      color: 'purple',
      example: 'my_variable_name'
    },
    {
      id: 'kebab-case',
      name: 'kebab-case',
      description: 'Lowercase with hyphens',
      icon: <List className="w-4 h-4" />,
      color: 'red',
      example: 'my-variable-name'
    },
    {
      id: 'SCREAMING_SNAKE_CASE',
      name: 'SCREAMING_SNAKE_CASE',
      description: 'Uppercase with underscores',
      icon: <Zap className="w-4 h-4" />,
      color: 'yellow',
      example: 'MY_CONSTANT_NAME'
    },
    {
      id: 'SCREAMING-KEBAB-CASE',
      name: 'SCREAMING-KEBAB-CASE',
      description: 'Uppercase with hyphens',
      icon: <Zap className="w-4 h-4" />,
      color: 'orange',
      example: 'MY-CONSTANT-NAME'
    },
    {
      id: 'dot.case',
      name: 'dot.case',
      description: 'Lowercase with dots',
      icon: <Code className="w-4 h-4" />,
      color: 'indigo',
      example: 'my.variable.name'
    },
    {
      id: 'path/case',
      name: 'path/case',
      description: 'Lowercase with forward slashes',
      icon: <Code className="w-4 h-4" />,
      color: 'pink',
      example: 'my/variable/name'
    },
    {
      id: 'Title Case',
      name: 'Title Case',
      description: 'Capitalize each word with spaces',
      icon: <Type className="w-4 h-4" />,
      color: 'teal',
      example: 'My Variable Name'
    },
    {
      id: 'Sentence case',
      name: 'Sentence case',
      description: 'First word capitalized, rest lowercase',
      icon: <Type className="w-4 h-4" />,
      color: 'cyan',
      example: 'My variable name'
    },
    {
      id: 'lowercase',
      name: 'lowercase',
      description: 'All lowercase with spaces',
      icon: <ArrowUpDown className="w-4 h-4" />,
      color: 'emerald',
      example: 'my variable name'
    },
    {
      id: 'UPPERCASE',
      name: 'UPPERCASE',
      description: 'All uppercase with spaces',
      icon: <ArrowUpDown className="w-4 h-4" />,
      color: 'violet',
      example: 'MY VARIABLE NAME'
    }
  ];

  const splitIntoWords = (text: string, customDelimiters: string): string[] => {
    // Default delimiters: space, underscore, hyphen, dot, slash, plus common punctuation
    let delimiters = /[\s_\-\.\/\\,;:!?@#$%^&*()+=\[\]{}|<>'"~`]/;
    
    // Add custom delimiters if provided
    if (customDelimiters.trim()) {
      const escapedCustom = customDelimiters.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      delimiters = new RegExp(`[\\s_\\-\\.\/\\\\,;:!?@#$%^&*()+=\\[\\]{}|<>'"~\`${escapedCustom}]`);
    }

    // Split by delimiters and camelCase boundaries
    let words: string[] = [];
    
    text.split(delimiters).forEach(segment => {
      if (!segment) return;
      
      // Split camelCase/PascalCase boundaries
      const camelCaseSplit = segment.split(/(?<=[a-z])(?=[A-Z])|(?<=[A-Z])(?=[A-Z][a-z])/);
      words.push(...camelCaseSplit.filter(word => word.length > 0));
    });

    return words;
  };

  const preserveSpecialPrefixSuffix = (original: string, converted: string, settings: ConversionSettings): string => {
    let result = converted;
    
    if (settings.preserveLeadingUnderscore && original.startsWith('_')) {
      const leadingUnderscores = original.match(/^_+/)?.[0] || '';
      result = leadingUnderscores + result;
    }
    
    if (settings.preserveTrailingUnderscore && original.endsWith('_')) {
      const trailingUnderscores = original.match(/_+$/)?.[0] || '';
      result = result + trailingUnderscores;
    }
    
    return result;
  };

  const detectAndPreserveAcronyms = (word: string, settings: ConversionSettings): string => {
    if (!settings.detectAcronyms) return word;
    
    // Common acronyms pattern (2+ consecutive uppercase letters)
    if (/^[A-Z]{2,}$/.test(word)) {
      return word; // Keep acronyms as-is for certain conversions
    }
    
    return word;
  };

  const convertText = (text: string, method: string, options: ConversionSettings): string => {
    if (!text.trim()) return '';
    
    const words = splitIntoWords(text, options.customDelimiters)
      .filter(word => word.length > 0)
      .map(word => options.trimWhitespace ? word.trim() : word)
      .filter(word => word.length >= options.minLength && word.length <= options.maxLength);

    if (words.length === 0) return '';

    let result: string;

    switch (method) {
      case 'camelCase':
        result = words.map((word, index) => {
          if (index === 0) {
            return options.forceFirstLowercase ? word.toLowerCase() : word;
          }
          const preservedAcronym = detectAndPreserveAcronyms(word, options);
          if (preservedAcronym === word && /^[A-Z]{2,}$/.test(word)) {
            return word; // Keep short acronyms
          }
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join('');
        break;

      case 'PascalCase':
        result = words.map(word => {
          const preservedAcronym = detectAndPreserveAcronyms(word, options);
          if (preservedAcronym === word && /^[A-Z]{2,}$/.test(word)) {
            return word;
          }
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join('');
        break;

      case 'snake_case':
        result = words.map(word => word.toLowerCase()).join('_');
        break;

      case 'kebab-case':
        result = words.map(word => word.toLowerCase()).join('-');
        break;

      case 'SCREAMING_SNAKE_CASE':
        result = words.map(word => word.toUpperCase()).join('_');
        break;

      case 'SCREAMING-KEBAB-CASE':
        result = words.map(word => word.toUpperCase()).join('-');
        break;

      case 'dot.case':
        result = words.map(word => word.toLowerCase()).join('.');
        break;

      case 'path/case':
        result = words.map(word => word.toLowerCase()).join('/');
        break;

      case 'Title Case':
        result = words.map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
        break;

      case 'Sentence case':
        result = words.map((word, index) => 
          index === 0 
            ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            : word.toLowerCase()
        ).join(' ');
        break;

      case 'lowercase':
        result = words.map(word => word.toLowerCase()).join(' ');
        break;

      case 'UPPERCASE':
        result = words.map(word => word.toUpperCase()).join(' ');
        break;

      default:
        result = text;
    }

    // Apply prefix and suffix
    if (options.addPrefix) result = options.addPrefix + result;
    if (options.addSuffix) result = result + options.addSuffix;

    // Preserve special underscores
    result = preserveSpecialPrefixSuffix(text, result, options);

    return result;
  };

  const executeConversion = (text: string, method: string, options: ConversionSettings): ConversionResult => {
    if (!text.trim()) {
      return {
        inputItems: [],
        finalItems: [],
        method,
        output: '',
        stats: {
          originalCount: 0,
          finalCount: 0,
          emptyRemoved: 0,
          duplicatesRemoved: 0,
          totalInputLength: 0,
          totalOutputLength: 0,
          avgInputLength: 0,
          avgOutputLength: 0,
          longestInput: '',
          shortestInput: '',
          longestOutput: '',
          shortestOutput: '',
          conversionRatio: 0
        }
      };
    }

    // Split input into items
    const inputItems = text.split('\n').map(item => item.replace(/\r$/, ''));
    let processedItems = [...inputItems];

    // Apply preprocessing
    if (options.trimWhitespace) {
      processedItems = processedItems.map(item => item.trim());
    }

    if (options.removeEmpty) {
      processedItems = processedItems.filter(item => item !== '');
    }

    // Filter by length
    processedItems = processedItems.filter(item => 
      item.length >= options.minLength && item.length <= options.maxLength
    );

    // Remove duplicates
    if (options.removeDuplicates) {
      if (options.ignoreCase) {
        const seenLower = new Set<string>();
        processedItems = processedItems.filter(item => {
          const lowerItem = item.toLowerCase();
          if (seenLower.has(lowerItem)) {
            return false;
          }
          seenLower.add(lowerItem);
          return true;
        });
      } else {
        processedItems = Array.from(new Set(processedItems));
      }
    }

    // Sort items
    if (options.sortItems) {
      processedItems = processedItems.sort((a, b) => {
        const comparison = options.ignoreCase 
          ? a.toLowerCase().localeCompare(b.toLowerCase())
          : a.localeCompare(b);
        return options.reverseSort ? -comparison : comparison;
      });
    }

    // Limit count
    if (options.limitCount && options.maxCount > 0) {
      processedItems = processedItems.slice(0, options.maxCount);
    }

    // Convert each item
    const convertedItems = processedItems.map(item => convertText(item, method, options));
    const output = convertedItems.join('\n');

    // Calculate statistics
    const stats = computeStatistics(inputItems, processedItems, convertedItems);

    return {
      inputItems,
      finalItems: convertedItems,
      method,
      output,
      stats
    };
  };

  const computeStatistics = (inputItems: string[], processedItems: string[], outputItems: string[]): ConversionStatistics => {
    const emptyRemoved = inputItems.filter(item => item.trim() === '').length;
    const duplicatesRemoved = inputItems.length - new Set(inputItems).size;
    
    const inputLengths = processedItems.map(item => item.length);
    const outputLengths = outputItems.map(item => item.length);
    
    const totalInputLength = inputLengths.reduce((sum, len) => sum + len, 0);
    const totalOutputLength = outputLengths.reduce((sum, len) => sum + len, 0);
    
    const avgInputLength = inputLengths.length > 0 ? Math.round(totalInputLength / inputLengths.length) : 0;
    const avgOutputLength = outputLengths.length > 0 ? Math.round(totalOutputLength / outputLengths.length) : 0;
    
    const longestInput = processedItems.reduce((longest, item) => item.length > longest.length ? item : longest, '');
    const shortestInput = processedItems.reduce((shortest, item) => item.length < shortest.length ? item : shortest, processedItems[0] || '');
    
    const longestOutput = outputItems.reduce((longest, item) => item.length > longest.length ? item : longest, '');
    const shortestOutput = outputItems.reduce((shortest, item) => item.length < shortest.length ? item : shortest, outputItems[0] || '');
    
    const conversionRatio = totalInputLength > 0 ? Math.round((totalOutputLength / totalInputLength) * 100) / 100 : 0;

    return {
      originalCount: inputItems.length,
      finalCount: outputItems.length,
      emptyRemoved,
      duplicatesRemoved,
      totalInputLength,
      totalOutputLength,
      avgInputLength,
      avgOutputLength,
      longestInput,
      shortestInput,
      longestOutput,
      shortestOutput,
      conversionRatio
    };
  };

  const conversionResult: ConversionResult = useMemo(() => {
    return executeConversion(inputText, selectedMethod, settings);
  }, [inputText, selectedMethod, settings]);

  useEffect(() => {
    setOutputText(conversionResult.output);
  }, [conversionResult]);

  const copyOutput = async () => {
    if (outputText) {
      await navigator.clipboard.writeText(outputText);
      setCopyMessage(true);
      setTimeout(() => setCopyMessage(false), 2000);
    }
  };

  const downloadOutput = () => {
    if (outputText) {
      const summary = `CamelCase Converter Results:
Method: ${conversionMethods.find(m => m.id === selectedMethod)?.name}
Original Items: ${conversionResult.stats.originalCount}
Final Items: ${conversionResult.stats.finalCount}
Total Input Length: ${conversionResult.stats.totalInputLength}
Total Output Length: ${conversionResult.stats.totalOutputLength}
Conversion Ratio: ${conversionResult.stats.conversionRatio}

Output:
${outputText}`;

      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'camelcase-converter-output.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleTexts = () => {
    const sampleSets = [
      'user_name\npassword_field\nemail_address\nfirst_name\nlast_name\nphone_number',
      'MY_CONSTANT_VALUE\nAPI_BASE_URL\nMAX_RETRY_COUNT\nDEFAULT_TIMEOUT\nERROR_MESSAGES',
      'get-user-data\nset-password\nvalidate-email\ncreate-account\ndelete-user\nupdate-profile',
      'UserAccountManager\nDatabaseConnection\nEmailValidator\nPasswordGenerator\nLoginController',
      'html element\ncss property\njavascript function\nreact component\nvue directive\nangular service',
      'home page\nabout us\ncontact form\nproduct list\nshopping cart\nuser profile',
      'firstName\nlastName\nemailAddress\nphoneNumber\ndateOfBirth\naddressLine1\naddressLine2',
      'button.click.handler\nform.submit.event\npage.load.complete\nuser.login.success\ndata.fetch.error',
      'CREATE_USER_SUCCESS\nUPDATE_PROFILE_PENDING\nDELETE_ACCOUNT_FAILURE\nFETCH_DATA_REQUEST\nLOGIN_EXPIRED',
      'my awesome variable\nsuper cool function\nreally long method name\ncomplex business logic\nuser interface component'
    ];
    setInputText(sampleSets[Math.floor(Math.random() * sampleSets.length)]);
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <Type className="text-purple-600" />
            CamelCase Converter
          </h1>
          <p className="text-gray-600">Convert text between different naming conventions with advanced options</p>
        </div>

        {/* Configuration Panel */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Conversion Configuration</h2>
            <div className="flex gap-2">
              <button
                onClick={generateSampleTexts}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Text
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Settings size={16} />
                Settings
              </button>
              <button
                onClick={() => setShowStatistics(!showStatistics)}
                className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Statistics
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                Current Method
              </label>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="font-semibold text-gray-800">
                  {conversionMethods.find(m => m.id === selectedMethod)?.name}
                </div>
                <div className="text-sm text-gray-600">
                  Example: {conversionMethods.find(m => m.id === selectedMethod)?.example}
                </div>
              </div>
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <p>Items processed: <span className="font-semibold">{conversionResult.stats.finalCount}</span></p>
                <p>Conversion ratio: <span className="font-semibold">{conversionResult.stats.conversionRatio}x</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-800">Conversion Settings</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Text Processing</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.trimWhitespace}
                    onChange={(e) => setSettings({...settings, trimWhitespace: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Trim Whitespace</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.removeEmpty}
                    onChange={(e) => setSettings({...settings, removeEmpty: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Remove Empty Lines</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.removeDuplicates}
                    onChange={(e) => setSettings({...settings, removeDuplicates: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Remove Duplicates</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.ignoreCase}
                    onChange={(e) => setSettings({...settings, ignoreCase: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Ignore Case for Duplicates</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.sortItems}
                    onChange={(e) => setSettings({...settings, sortItems: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Sort Items</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.reverseSort}
                    onChange={(e) => setSettings({...settings, reverseSort: e.target.checked})}
                    disabled={!settings.sortItems}
                    className="w-4 h-4 text-blue-600 disabled:opacity-50"
                  />
                  <span className="text-sm">Reverse Sort</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Conversion Options</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.preserveNumbers}
                    onChange={(e) => setSettings({...settings, preserveNumbers: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Preserve Numbers</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.preserveAcronyms}
                    onChange={(e) => setSettings({...settings, preserveAcronyms: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Preserve Acronyms</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.detectAcronyms}
                    onChange={(e) => setSettings({...settings, detectAcronyms: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Auto-detect Acronyms</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.forceFirstLowercase}
                    onChange={(e) => setSettings({...settings, forceFirstLowercase: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Force First Word Lowercase (camelCase)</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.preserveLeadingUnderscore}
                    onChange={(e) => setSettings({...settings, preserveLeadingUnderscore: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Preserve Leading Underscores</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.preserveTrailingUnderscore}
                    onChange={(e) => setSettings({...settings, preserveTrailingUnderscore: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Preserve Trailing Underscores</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Custom Delimiters</label>
                  <input
                    type="text"
                    value={settings.customDelimiters}
                    onChange={(e) => setSettings({...settings, customDelimiters: e.target.value})}
                    placeholder="Additional word separators..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Output Formatting</h3>
                
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Add Prefix</label>
                  <input
                    type="text"
                    value={settings.addPrefix}
                    onChange={(e) => setSettings({...settings, addPrefix: e.target.value})}
                    placeholder="Prefix for each item..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Add Suffix</label>
                  <input
                    type="text"
                    value={settings.addSuffix}
                    onChange={(e) => setSettings({...settings, addSuffix: e.target.value})}
                    placeholder="Suffix for each item..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Min Length</label>
                  <input
                    type="number"
                    min="0"
                    value={settings.minLength}
                    onChange={(e) => setSettings({...settings, minLength: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Max Length</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.maxLength}
                    onChange={(e) => setSettings({...settings, maxLength: parseInt(e.target.value) || 200})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                  />
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.limitCount}
                    onChange={(e) => setSettings({...settings, limitCount: e.target.checked})}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm">Limit Item Count</span>
                </label>

                {settings.limitCount && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Max Items</label>
                    <input
                      type="number"
                      min="1"
                      value={settings.maxCount}
                      onChange={(e) => setSettings({...settings, maxCount: parseInt(e.target.value) || 100})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Input and Output */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Input Text</label>
                <div className="text-sm text-gray-500">
                  {inputText.split('\n').filter(line => line.trim()).length} items
                </div>
              </div>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter text to convert (one item per line)...&#10;Examples:&#10;user_name&#10;MY_CONSTANT&#10;kebab-case-text&#10;camelCaseText&#10;PascalCaseText"
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none resize-none text-gray-700 transition-colors font-mono text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Converted Output</label>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-500">
                    {outputText.length} characters
                  </div>
                  <button
                    onClick={copyOutput}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Copy size={16} />
                    {copyMessage ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadOutput}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
              <textarea
                value={outputText}
                readOnly
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none font-mono text-sm"
                placeholder="Converted output will appear here..."
              />
            </div>
          </div>
        </div>

        {/* Conversion Methods */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Type className="text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Conversion Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {conversionMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethod === method.id
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedMethod === method.id ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedMethod === method.id && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm mb-2 ${
                  selectedMethod === method.id ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
                <div className={`text-xs font-mono p-2 rounded ${
                  selectedMethod === method.id ? 'bg-white bg-opacity-50' : 'bg-gray-100'
                }`}>
                  {method.example}
                </div>
              </div>
            ))}
          </div>

          {/* Method Help */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-semibold text-gray-700 mb-2">
              {conversionMethods.find(m => m.id === selectedMethod)?.name} Guide
            </h3>
            <div className="text-sm text-gray-600">
              {selectedMethod === 'camelCase' && (
                <div>
                  <p>• First word lowercase, subsequent words capitalized</p>
                  <p>• Common in JavaScript, Java, C# variables</p>
                  <p>• Example: "user name" → "userName"</p>
                </div>
              )}
              {selectedMethod === 'PascalCase' && (
                <div>
                  <p>• All words capitalized, no separators</p>
                  <p>• Common for class names, components</p>
                  <p>• Example: "user profile" → "UserProfile"</p>
                </div>
              )}
              {selectedMethod === 'snake_case' && (
                <div>
                  <p>• Lowercase words separated by underscores</p>
                  <p>• Common in Python, Ruby, databases</p>
                  <p>• Example: "user name" → "user_name"</p>
                </div>
              )}
              {selectedMethod === 'kebab-case' && (
                <div>
                  <p>• Lowercase words separated by hyphens</p>
                  <p>• Common in URLs, CSS classes, HTML attributes</p>
                  <p>• Example: "user name" → "user-name"</p>
                </div>
              )}
              {selectedMethod === 'SCREAMING_SNAKE_CASE' && (
                <div>
                  <p>• Uppercase words separated by underscores</p>
                  <p>• Common for constants, environment variables</p>
                  <p>• Example: "api key" → "API_KEY"</p>
                </div>
              )}
              {selectedMethod === 'SCREAMING-KEBAB-CASE' && (
                <div>
                  <p>• Uppercase words separated by hyphens</p>
                  <p>• Less common, used in specific contexts</p>
                  <p>• Example: "api key" → "API-KEY"</p>
                </div>
              )}
              {selectedMethod === 'dot.case' && (
                <div>
                  <p>• Lowercase words separated by dots</p>
                  <p>• Common in package names, file extensions</p>
                  <p>• Example: "user name" → "user.name"</p>
                </div>
              )}
              {selectedMethod === 'path/case' && (
                <div>
                  <p>• Lowercase words separated by forward slashes</p>
                  <p>• Common in file paths, URLs</p>
                  <p>• Example: "user name" → "user/name"</p>
                </div>
              )}
              {selectedMethod === 'Title Case' && (
                <div>
                  <p>• Each word capitalized with spaces</p>
                  <p>• Common in titles, headings</p>
                  <p>• Example: "user name" → "User Name"</p>
                </div>
              )}
              {selectedMethod === 'Sentence case' && (
                <div>
                  <p>• First word capitalized, rest lowercase</p>
                  <p>• Common in sentences, descriptions</p>
                  <p>• Example: "user name" → "User name"</p>
                </div>
              )}
              {selectedMethod === 'lowercase' && (
                <div>
                  <p>• All words lowercase with spaces</p>
                  <p>• Common for user-readable text</p>
                  <p>• Example: "User Name" → "user name"</p>
                </div>
              )}
              {selectedMethod === 'UPPERCASE' && (
                <div>
                  <p>• All words uppercase with spaces</p>
                  <p>• Common for emphasis, headers</p>
                  <p>• Example: "user name" → "USER NAME"</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        {showStatistics && inputText && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-green-600" />
              <h2 className="text-xl font-semibold text-gray-800">Conversion Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Original Items</h3>
                <div className="text-2xl font-bold text-blue-900">{conversionResult.stats.originalCount}</div>
                <p className="text-sm text-blue-600">Input items</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Final Items</h3>
                <div className="text-2xl font-bold text-green-900">{conversionResult.stats.finalCount}</div>
                <p className="text-sm text-green-600">After processing</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Total Length</h3>
                <div className="text-2xl font-bold text-orange-900">{conversionResult.stats.totalOutputLength}</div>
                <p className="text-sm text-orange-600">Output characters</p>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Conversion Ratio</h3>
                <div className="text-2xl font-bold text-purple-900">{conversionResult.stats.conversionRatio}x</div>
                <p className="text-sm text-purple-600">Length change</p>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Avg Input Length</h4>
                <div className="text-lg font-bold text-gray-900">{conversionResult.stats.avgInputLength}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Avg Output Length</h4>
                <div className="text-lg font-bold text-gray-900">{conversionResult.stats.avgOutputLength}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Empty Removed</h4>
                <div className="text-lg font-bold text-gray-900">{conversionResult.stats.emptyRemoved}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Duplicates Removed</h4>
                <div className="text-lg font-bold text-gray-900">{conversionResult.stats.duplicatesRemoved}</div>
              </div>
            </div>

            {/* Item Analysis */}
            {conversionResult.stats.longestInput && conversionResult.stats.shortestInput && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-700 mb-3">Item Analysis:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <h4 className="font-semibold text-emerald-800 mb-1">
                        Longest Input ({conversionResult.stats.longestInput.length} chars)
                      </h4>
                      <div className="text-sm text-emerald-700 font-mono truncate max-w-full">
                        {conversionResult.stats.longestInput}
                      </div>
                    </div>
                    <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                      <h4 className="font-semibold text-teal-800 mb-1">
                        Shortest Input ({conversionResult.stats.shortestInput.length} chars)
                      </h4>
                      <div className="text-sm text-teal-700 font-mono truncate max-w-full">
                        {conversionResult.stats.shortestInput || '(empty)'}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                      <h4 className="font-semibold text-indigo-800 mb-1">
                        Longest Output ({conversionResult.stats.longestOutput.length} chars)
                      </h4>
                      <div className="text-sm text-indigo-700 font-mono truncate max-w-full">
                        {conversionResult.stats.longestOutput}
                      </div>
                    </div>
                    <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
                      <h4 className="font-semibold text-cyan-800 mb-1">
                        Shortest Output ({conversionResult.stats.shortestOutput.length} chars)
                      </h4>
                      <div className="text-sm text-cyan-700 font-mono truncate max-w-full">
                        {conversionResult.stats.shortestOutput || '(empty)'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Preview */}
        {conversionResult.finalItems.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <List className="text-indigo-600" />
              <h2 className="text-xl font-semibold text-gray-800">Conversion Preview</h2>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto mb-6">
              {conversionResult.inputItems.slice(0, 20).map((item, index) => {
                const outputItem = conversionResult.finalItems[index];
                return (
                  <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500 font-mono">#{index + 1}</span>
                      <div className="text-xs text-gray-500">
                        {item.length} → {outputItem?.length || 0} chars
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Input:</div>
                        <div className="text-sm font-mono text-gray-700 bg-white p-2 rounded border">
                          {item || '(empty)'}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Output:</div>
                        <div className="text-sm font-mono text-purple-700 bg-purple-50 p-2 rounded border border-purple-200">
                          {outputItem || '(empty)'}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {conversionResult.inputItems.length > 20 && (
                <div className="text-sm text-gray-500 text-center py-2">
                  ...and {conversionResult.inputItems.length - 20} more items
                </div>
              )}
            </div>

            {/* Quick Conversion Test */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
              <h3 className="font-semibold text-purple-700 mb-2">Quick Test Examples:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                {[
                  'hello world',
                  'user_name_field',
                  'MY_CONSTANT_VALUE',
                  'kebab-case-text',
                  'PascalCaseExample',
                  'camelCaseVariable'
                ].map((example, index) => (
                  <div key={index} className="bg-white p-2 rounded border">
                    <div className="text-gray-600 font-mono text-xs">{example}</div>
                    <div className="text-purple-700 font-mono font-semibold">
                      {convertText(example, selectedMethod, settings)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CamelCaseConverter;